import { useEffect, useState, useCallback } from "react";
import { fetchVehicles, createVehicle, updateVehicle, deleteVehicle, fetchClients, lookupVehiclePlaca } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, MagnifyingGlass, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import VehicleHistoryDialog from "@/components/VehicleHistoryDialog";

const empty = { client_id: "", placa: "", modelo: "", ano: "", motorizacao: "", chassi: "", quilometragem: 0 };

export default function Vehicles({ embedded = false, prefillClientId = null, onPrefillConsumed }) {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [historyVehicle, setHistoryVehicle] = useState(null);

  const reload = useCallback(
    () => Promise.all([fetchVehicles(), fetchClients()]).then(([v, c]) => { setList(v); setClients(c); }),
    []
  );
  useEffect(() => { reload(); }, [reload]);

  // Auto-open new vehicle dialog when a prefill client id is provided
  useEffect(() => {
    if (prefillClientId) {
      setEditId(null);
      setForm({ ...empty, client_id: prefillClientId });
      setOpen(true);
      onPrefillConsumed?.();
    }
  }, [prefillClientId, onPrefillConsumed]);

  const clientName = (id) => clients.find((c) => c.id === id)?.nome || "Cliente removido";

  const onLookupPlaca = async () => {
    if (!form.placa) { toast.error("Digite a placa primeiro"); return; }
    try {
      const data = await lookupVehiclePlaca(form.placa);
      setForm((f) => ({
        ...f,
        modelo: data.modelo || f.modelo,
        ano: data.ano || f.ano,
        motorizacao: data.motorizacao || f.motorizacao,
        chassi: data.chassi || f.chassi,
      }));
      toast.success("Dados do veículo preenchidos");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível consultar a placa");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.placa || !form.modelo) { toast.error("Cliente, placa e modelo são obrigatórios"); return; }
    const payload = { ...form, quilometragem: parseInt(form.quilometragem || 0, 10) };
    try {
      if (editId) await updateVehicle(editId, payload);
      else await createVehicle(payload);
      toast.success("Veículo salvo");
      setOpen(false); setForm(empty); setEditId(null);
      reload();
    } catch { toast.error("Erro ao salvar"); }
  };

  const onEdit = (v) => {
    setEditId(v.id);
    setForm({ ...v });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir este veículo?")) return;
    await deleteVehicle(id);
    toast.success("Veículo excluído");
    reload();
  };

  const newVehicleDialog = (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
      <DialogTrigger asChild>
        <Button data-testid="new-vehicle-btn" className="bg-[#001E50] hover:bg-[#001740] text-white">
          <Plus size={16} weight="bold" className="mr-1.5" /> Novo Veículo
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-heading">{editId ? "Editar Veículo" : "Novo Veículo"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Cliente *</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="vehicle-client"><SelectValue placeholder="Selecione um cliente" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Placa *</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="vehicle-placa"
                  value={form.placa}
                  onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase() })}
                  placeholder="ABC1D23"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={onLookupPlaca}
                  data-testid="lookup-placa-btn"
                  title="Consultar dados pela placa"
                >
                  <MagnifyingGlass size={14} className="mr-1" /> Consultar
                </Button>
              </div>
              <p className="text-[10px] text-[#475569] mt-1">Use o botão "Consultar" para preencher modelo, ano, motorização e chassi automaticamente (requer API configurada).</p>
            </div>
            <div>
              <Label>Modelo *</Label>
              <Input data-testid="vehicle-modelo" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} />
            </div>
            <div>
              <Label>Ano</Label>
              <Input data-testid="vehicle-ano" value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value })} />
            </div>
            <div>
              <Label>Motorização</Label>
              <Input data-testid="vehicle-motor" placeholder="ex: 1.0 Turbo TSI" value={form.motorizacao} onChange={(e) => setForm({ ...form, motorizacao: e.target.value })} />
            </div>
            <div>
              <Label>Chassi</Label>
              <Input data-testid="vehicle-chassi" placeholder="17 caracteres" maxLength={17} value={form.chassi} onChange={(e) => setForm({ ...form, chassi: e.target.value.toUpperCase() })} />
            </div>
            <div className="col-span-2">
              <Label>Quilometragem</Label>
              <Input data-testid="vehicle-km" type="number" value={form.quilometragem} onChange={(e) => setForm({ ...form, quilometragem: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" data-testid="save-vehicle-btn" className="bg-[#001E50] hover:bg-[#001740] text-white">Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <div data-testid="vehicles-page">
      {!embedded && (
        <PageHeader
          title="Veículos"
          subtitle="Frota dos clientes cadastrada na oficina."
          actions={newVehicleDialog}
        />
      )}
      {embedded && (
        <div className="flex justify-end mb-4">
          {newVehicleDialog}
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-md">
        {list.length === 0 ? (
          <EmptyState title="Nenhum veículo cadastrado" message="Cadastre um cliente primeiro e depois adicione seu veículo." />
        ) : (
          <Table>
            <TableHeader className="bg-[#F8FAFC]">
              <TableRow>
                <TableHead>Placa</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead>Ano</TableHead>
                <TableHead>Motorização</TableHead>
                <TableHead>Chassi</TableHead>
                <TableHead className="text-right">KM</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((v, i) => (
                <TableRow key={v.id} className={i % 2 ? "bg-[#F8FAFC]" : ""} data-testid={`vehicle-row-${v.id}`}>
                  <TableCell className="font-mono font-semibold text-[#001E50]">{v.placa}</TableCell>
                  <TableCell className="font-medium">{v.modelo}</TableCell>
                  <TableCell className="text-[#475569]">{v.ano || "-"}</TableCell>
                  <TableCell className="text-[#475569]">{v.motorizacao || "-"}</TableCell>
                  <TableCell className="text-[#475569] font-mono text-xs">{v.chassi || "-"}</TableCell>
                  <TableCell className="text-right tabular-nums">{(v.quilometragem || 0).toLocaleString("pt-BR")}</TableCell>
                  <TableCell className="text-[#475569]">{clientName(v.client_id)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => setHistoryVehicle(v)} data-testid={`history-vehicle-${v.id}`} title="Histórico de OS">
                      <ClockCounterClockwise size={16} className="text-[#00B0F0]" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onEdit(v)} data-testid={`edit-vehicle-${v.id}`}>
                      <PencilSimple size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onDelete(v.id)} data-testid={`delete-vehicle-${v.id}`}>
                      <Trash size={16} className="text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <VehicleHistoryDialog
        vehicle={historyVehicle}
        open={!!historyVehicle}
        onOpenChange={(v) => { if (!v) setHistoryVehicle(null); }}
      />
    </div>
  );
}
